/* eslint-disable react-hooks/exhaustive-deps */
import * as React from "react";
import { useQuery, useLazyQuery } from "@apollo/client";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import MyBreadcrumbs from "../MyBreadcrumbs";
import { CLIENTS } from "../../graphql/Client";
import { GET_PROFIT_ITEM_REPORT } from "../../graphql/Dashboard";
import ProfitItemReportPDF from "../PDF/ProfitItemReportPDF";
import { generateArrayOfYears } from "../../utils";

const rupiah = (value) => `Rp. ${(Number(value) || 0).toLocaleString("id-ID")}`;

export default function ProfitItemReportPage(props) {
  const [year, setYear] = React.useState(new Date().getFullYear());
  const [client, setClient] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const years = generateArrayOfYears();

  /* FETCH DATA CLIENTS */
  const { data: dataClients } = useQuery(CLIENTS, {
    fetchPolicy: "network-only",
    onError: (e) => console.log("error getting clients:", e),
  });

  const clients = React.useMemo(
    () => dataClients?.clients ?? [],
    [dataClients]
  );

  const [getReport, { loading }] = useLazyQuery(GET_PROFIT_ITEM_REPORT, {
    fetchPolicy: "network-only",
  });

  const handleSearch = async () => {
    const data = await getReport({
      variables: {
        year,
        client_id: client?.id ?? null,
      },
    });

    setRows(
      data?.data?.getProfitItemReport?.map((x, i) => ({ ...x, no: i + 1 })) ??
        []
    );
  };

  const handleDownload = () => {
    ProfitItemReportPDF(rows, year, client?.name ?? "Semua");
  };

  const grandTotalBasePrice = rows.reduce(
    (n, x) => Number(n) + (Number(x.total_base_price) || 0),
    0
  );
  const grandTotalSales = rows.reduce(
    (n, x) => Number(n) + (Number(x.total_sales) || 0),
    0
  );
  const grandTotalProfit = rows.reduce(
    (n, x) => Number(n) + (Number(x.total_profit) || 0),
    0
  );

  const columns = [
    {
      field: "no",
      headerName: "No.",
      width: 60,
      renderCell: (params) => `${params.row.no}.`,
    },
    {
      field: "created_at",
      headerName: "Tanggal",
      width: 110,
      valueGetter: (params) =>
        params.row.created_at
          ? dayjs(params.row.created_at).format("DD-MM-YYYY")
          : "-",
    },
    {
      field: "oid",
      headerName: "No. Order",
      width: 110,
    },
    {
      field: "client_name",
      headerName: "Pelanggan",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "item_name",
      headerName: "Nama Item",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "qty",
      headerName: "Qty",
      width: 70,
    },
    {
      field: "base_price",
      headerName: "Harga Kulak",
      width: 120,
      align: "right",
      valueGetter: (params) => rupiah(params.row.base_price),
    },
    {
      field: "total_base_price",
      headerName: "Total Kulak",
      width: 130,
      align: "right",
      valueGetter: (params) => rupiah(params.row.total_base_price),
    },
    {
      field: "selling_price",
      headerName: "Harga Jual",
      width: 120,
      align: "right",
      valueGetter: (params) => rupiah(params.row.selling_price),
    },
    {
      field: "total_sales",
      headerName: "Total Jual",
      width: 130,
      align: "right",
      valueGetter: (params) => rupiah(params.row.total_sales),
    },
    {
      field: "total_profit",
      headerName: "Laba",
      width: 130,
      align: "right",
      valueGetter: (params) => rupiah(params.row.total_profit),
    },
  ];

  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          bgcolor: "background.paper",
          borderRadius: 1,
          alignItems: "center",
        }}
      >
        <Box sx={{ flexGrow: 1, mb: 3 }}>
          <MyBreadcrumbs data={["Laporan", "Laba Per Item"]} />
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          bgcolor: "background.paper",
          borderRadius: 1,
          alignItems: "center",
          width: "100%",
          mb: 1,
        }}
      >
        <Box sx={{ p: 0.5, m: 0.5, width: "15%" }}>
          <FormControl fullWidth>
            <InputLabel id='year'>Tahun</InputLabel>
            <Select
              labelId='year'
              id='year'
              name='year'
              label='Tahun'
              value={year}
              onChange={(e) => setYear(e.target.value)}
            >
              {years.map((x, i) => (
                <MenuItem key={i} value={x}>
                  {x}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ p: 0.5, m: 0.5, width: "35%" }}>
          <Autocomplete
            options={clients}
            value={client}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, v) => setClient(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Nama Pelanggan (kosongkan untuk semua)'
              />
            )}
          />
        </Box>
        <Box sx={{ p: 0.5, m: 0.5 }}>
          <Button variant='contained' onClick={handleSearch} disabled={loading}>
            Cari
          </Button>
        </Box>
        <Box sx={{ p: 0.5, m: 0.5 }}>
          <Button
            variant='contained'
            color='secondary'
            onClick={handleDownload}
            disabled={!rows.length}
          >
            Download PDF
          </Button>
        </Box>
      </Box>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <div style={{ height: 500, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.item_id}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            loading={loading}
          />
        </div>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            p: 2,
            bgcolor: "background.paper",
          }}
        >
          <Typography variant='subtitle1' fontWeight='bold'>
            Total Kulak: {rupiah(grandTotalBasePrice)}
          </Typography>
          <Typography variant='subtitle1' fontWeight='bold'>
            Total Jual: {rupiah(grandTotalSales)}
          </Typography>
          <Typography variant='subtitle1' fontWeight='bold'>
            Total Laba: {rupiah(grandTotalProfit)}
          </Typography>
        </Box>
      </Paper>
    </React.Fragment>
  );
}
